import { getPassengerTypes } from "../../../app/api/getPassengerType";
import { BaseAction } from "../../../core/actions/BaseAction";
import { getAgeRange } from "../../../Lib/Utils/validationUtil";
import { PassengerTypeEnum } from "../../../Orchestrator/Types/Enums/PassengerTypeEnum";
import { IApiResponse } from "../../../Orchestrator/Types/Index";
import { IPassengerType } from "../../../Orchestrator/Types/Interface/IPassengerType";

class GetPassengerTypesAction extends BaseAction<IPassengerType[], void> {
    // Several flows (HomePage, MainBooking, Checkout, ...) call execute() during
    // startup; coalesce concurrent calls into one network request.
    protected override deduplicate = true;
    private cachedResult: IPassengerType[] | null = null;

    transformTypes = (passengerTypes: IPassengerType[]) => {
        const order = [
            PassengerTypeEnum.adult,
            PassengerTypeEnum.child,
            PassengerTypeEnum.infant,
            PassengerTypeEnum.senior,
            PassengerTypeEnum.student,
            PassengerTypeEnum.prm,
            PassengerTypeEnum.wheelchair
        ];

        const transformed = [];

        // Sort passengerTypes based on the predefined order (using the enum)
        const sortedPassengerTypes = passengerTypes.sort((a: any, b: any) => {
            return order.indexOf(a.value) - order.indexOf(b.value);
        });
        // Transform the sorted list
        for (let i = 0; i < sortedPassengerTypes.length; i++) {
            const type = sortedPassengerTypes[i];
            const transformedType = {
                id: "",
                descriptionLabelId: "",
                shortDescription: "",
                shortDescriptionLabelId: "",
                description: "",
                year: "",
                ageRange: "",
                ...type
            };

            switch (type.value) {
                case PassengerTypeEnum.infant:
                    transformedType.id = "infants";
                    transformedType.year = "(0 - 2 years)";
                    transformedType.descriptionLabelId = "INFANT_DESC";
                    transformedType.shortDescriptionLabelId = "INFANT_SHORT_DESC";
                    transformedType.ageRange = getAgeRange(type)
                    break;
                case PassengerTypeEnum.child:
                    transformedType.id = "children";
                    transformedType.year = "(0 - 12 years)";
                    transformedType.descriptionLabelId = "CHILD_DESC";
                    transformedType.ageRange = getAgeRange(type);
                    transformedType.shortDescriptionLabelId = "CHILD_SHORT_DESC";
                    break;
                case PassengerTypeEnum.adult:
                    transformedType.id = "adults";
                    transformedType.year = "(12+ years)";
                    transformedType.descriptionLabelId = "ADULT_DESC";
                    transformedType.ageRange = getAgeRange(type);
                    transformedType.shortDescriptionLabelId = "ADULT_SHORT_DESC";
                    break;
                case PassengerTypeEnum.senior:
                    transformedType.id = "seniorcitizens";
                    transformedType.year = "(60+ years)";
                    transformedType.descriptionLabelId = "SENIOR_DESC";
                    transformedType.ageRange = getAgeRange(type);
                    transformedType.shortDescriptionLabelId = "SENIOR_SHORT_DESC";
                    break;
                case PassengerTypeEnum.student:
                    transformedType.id = "students";
                    transformedType.year = "(<26 years)";
                    transformedType.descriptionLabelId = "STUDENT_DESC";
                    transformedType.ageRange = getAgeRange(type);
                    transformedType.shortDescriptionLabelId = "STUDENT_SHORT_DESC";
                    break;
                case PassengerTypeEnum.prm:
                    transformedType.id = "reducedMobility";
                    transformedType.year = "(People with Reduced Mobility)";
                    transformedType.ageRange = getAgeRange(type);
                    transformedType.descriptionLabelId = "PRM_DESC";
                    transformedType.shortDescriptionLabelId = "PRM_SHORT_DESC";
                    break;
                case PassengerTypeEnum.wheelchair:
                    transformedType.id = "wheelchair";
                    transformedType.year = "(People with Wheelchair)";
                    transformedType.descriptionLabelId = "WHEELCHAIR_DESC";
                    transformedType.shortDescriptionLabelId = "WHEELCHAIR_SHORT_DESC";
                    transformedType.ageRange = getAgeRange(type);
                    break;
            }

            transformed.push(transformedType);
        }

        return transformed;
    };

    // Overriding `run` and returning the Passenger Types
    override async run(): Promise<IPassengerType[] | null | any> {
        // Return cached result if already fetched during this session
        if (this.cachedResult) {
            return this.cachedResult;
        }

        let listOfPassengerType: IPassengerType[] = []
        try {
            const response: IApiResponse<{ answer: IPassengerType[] }> = await getPassengerTypes(false);
            if (response.status === "ok" && Array.isArray(response.answer)) {
                const passengerTypes = response.answer;
                if (passengerTypes.length > 0) {
                    listOfPassengerType = passengerTypes;
                } else {
                    console.warn("No Passenger Type available.");
                }
            } else {
                console.warn("Failed to fetch Passenger Type.");
            }
        } catch (error: any) {
            console.error("Error fetching Passenger Type:", error);
        } finally {
            const transformed = this.transformTypes(listOfPassengerType);
            if (listOfPassengerType.length > 0) {
                this.cachedResult = transformed;
            }
            return transformed;
        }
    }
}

export const passengerTypeAction = new GetPassengerTypesAction()